import { Alert, Button, Card, SkeletonRows } from "@station/ui";
import { createFileRoute, Link } from "@tanstack/react-router";
import { MonitorPlay } from "lucide-react";
import type { ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { useWashQueue } from "../../../features/wash/queries.js";
import { QueueBoard } from "../../../features/wash/queue-board.js";
import { washSearchShape, WashStationPage, type WashStationContext } from "../../../features/wash/shared.js";
import { errorMessage } from "../../../lib/errors.js";
import { formatDateTime } from "../../../lib/format.js";

const searchSchema = z.object(washSearchShape);

export const Route = createFileRoute("/_app/yikama/sira")({
  validateSearch: (search) => searchSchema.parse(search),
  component: QueueRoute,
});

/**
 * Sıra ekranı (Prompt 18 web, ADR 0018 §14) — yönetim panelindeki önizleme + TV için tam ekran bağlantısı
 * (`/sira-ekrani/yikama?stationId=…`, kabuksuz). Plaka maskeli, ad / telefon yok; `wash.work_order.status_changed`
 * olayında tazelenir.
 */
function QueueRoute(): ReactElement {
  const { t } = useTranslation();
  return (
    <WashStationPage
      title={t("menu.washQueue")}
      description={t("wash.queue.description")}
      actions={({ station }) => (
        <Button asChild>
          <Link to="/sira-ekrani/yikama" search={{ stationId: station.id }} target="_blank" rel="noreferrer">
            <MonitorPlay aria-hidden="true" />
            {t("wash.queue.openFullscreen")}
          </Link>
        </Button>
      )}
    >
      {(context) => <QueueBody context={context} />}
    </WashStationPage>
  );
}

function QueueBody({ context }: { context: WashStationContext }): ReactElement {
  const { t } = useTranslation();
  const queue = useWashQueue(context.station.id);
  if (queue.error) return <Alert tone="danger">{errorMessage(queue.error)}</Alert>;
  if (!queue.data) return <SkeletonRows rows={5} />;
  return (
    <>
      <Card className="flex flex-wrap items-center justify-between gap-2 p-4 text-sm">
        <span>{t("wash.queue.privacy")}</span>
        <span className="text-muted-foreground">
          {t("wash.queue.updatedAt", { at: formatDateTime(queue.data.generatedAt) })}
        </span>
      </Card>
      <QueueBoard queue={queue.data} variant="panel" />
    </>
  );
}
